import React from 'react';
import { Route as ReactDOMRoute, Redirect, RouteProps } from 'react-router-dom';
import jwt_decode from 'jwt-decode';

import { useAuth } from '~/hooks/auth';

import { PROJECT_PATHS } from '~/constants';

import Renderer from '~/pages/_renderer';

interface IRouteProps extends RouteProps {
  path: string;
  isPrivate?: boolean;
  component?: React.ComponentType;
  search?: string;
}

interface ITokenPayload {
  exp: number;
}

const Route: React.FC<IRouteProps> = ({
  path,
  isPrivate = false,
  component: Component,
  search,
  ...rest
}) => {
  const { user, signOut } = useAuth();
  let isUserLoggedIn = false;
  let passwordResetToken = '';

  function checkPrivateComponent(): React.ReactNode {
    return <Renderer path={path} component={Component} />;
  }

  function checkNonPrivateComponent(): React.ReactNode {
    if (Component) {
      return <Component />;
    }

    return (
      <Redirect
        to={{
          pathname: '/',
        }}
      />
    );
  }

  function checkPrivateCompWithNoPerms(): React.ReactNode {
    if (Component) {
      return <Component />;
    }

    return (
      <Redirect
        to={{
          pathname: '/',
        }}
      />
    );
  }

  const checkIsPasswordRequest = (): boolean => {
    return path.indexOf('/resetpassword/request') !== -1;
  };

  const checkIsPasswordReset = (): boolean => {
    return path.indexOf('/passwordreset') !== -1;
  };

  const checkIsExternalAccess = (): boolean => {
    return path.indexOf('/externalaccess') !== -1;
  };

  const checkIsNewProject = (): boolean => {
    return path.indexOf('/newproject') !== -1;
  };

  const checkIsExternalValidation = (): boolean => {
    return path.indexOf('/externalvalidation') !== -1;
  };

  const getPasswordResetInfo = (): void => {
    const splitPath = path.split('passwordreset', 2);

    if (splitPath && splitPath.length > 1) {
      const splitRoute = splitPath[1].split('/', 2);

      if (splitRoute && splitRoute.length > 1) {
        passwordResetToken = splitRoute[1];
      }
    }
  };

  const getExternalInfo = (): {
    project_type?: string;
    project_id?: string;
  } => {
    const splitPath = path.split('externalaccess', 2);

    if (splitPath && splitPath.length > 1) {
      const splitRoute = splitPath[1].split('/', 3);

      if (splitRoute && splitRoute.length > 2) {
        return { project_type: splitRoute[1], project_id: splitRoute[2] };
      }
    }

    return { project_type: undefined, project_id: undefined };
  };

  const getNewProjectInfo = (): {
    project_type: string;
    erp_client_code: string;
  } => {
    if (search) {
      const params = new URLSearchParams(search);
      const project_type = params.get('type');
      const erp_client_code = params.get('client');

      if (project_type && erp_client_code) {
        if (project_type in PROJECT_PATHS) {
          return { project_type, erp_client_code };
        }
      }
    }

    return { project_type: '', erp_client_code: '' };
  };

  function validateUser(): boolean {
    if (user) {
      const token = localStorage.getItem('@GrpsPDV:token');
      if (token) {
        const decoded = jwt_decode(token);
        const dateNow = new Date();

        const { exp } = decoded as ITokenPayload;

        if (exp * 1000 < dateNow.getTime()) {
          signOut();
        } else {
          return true;
        }

        return false;
      }
    }

    return false;
  }

  return (
    <ReactDOMRoute
      render={({ location }) => {
        isUserLoggedIn = validateUser();

        if (checkIsPasswordRequest()) {
          return checkNonPrivateComponent();
        }

        if (checkIsPasswordReset()) {
          getPasswordResetInfo();

          return (
            <Redirect
              to={{
                pathname: '/resetpassword/reset',
                state: { token: passwordResetToken },
              }}
            />
          );
        }

        if (checkIsExternalValidation()) {
          return checkPrivateCompWithNoPerms();
        }

        if (checkIsExternalAccess()) {
          const { project_type, project_id } = getExternalInfo();

          return (
            <Redirect
              to={{
                pathname: isUserLoggedIn ? '/externalvalidation' : '/',
                state: { type: 'existing_project', project_type, project_id },
              }}
            />
          );
        }

        if (checkIsNewProject()) {
          const { project_type, erp_client_code } = getNewProjectInfo();

          return (
            <Redirect
              to={{
                pathname: isUserLoggedIn ? '/externalvalidation' : '/',
                state: {
                  type: 'new_project',
                  project_type,
                  erp_client_code,
                },
              }}
            />
          );
        }

        if (isUserLoggedIn) {
          if (path === '/') {
            return (
              <Redirect
                to={{
                  pathname: '/dashboard',
                  state: { from: location },
                }}
              />
            );
          }

          return checkPrivateComponent();
        }

        if (!isPrivate) {
          return checkNonPrivateComponent();
        }

        return (
          <Redirect
            to={{
              pathname: '/',
              state: { from: location },
            }}
          />
        );
      }}
      {...rest}
    />
  );
};

export default Route;
